import { useState } from 'react';
import { aoiApi, type PinAoiInput } from '../services/aoiApi';

interface PinAoiModalProps {
  onClose: () => void;
  onPinned: (id: string) => void;
}

interface TrsRow {
  township_num: string;
  township_dir: 'N' | 'S';
  range_num: string;
  range_dir: 'E' | 'W';
  section: string;
}

const blankRow = (): TrsRow => ({
  township_num: '',
  township_dir: 'N',
  range_num: '',
  range_dir: 'W',
  section: '',
});

export function PinAoiModal({ onClose, onPinned }: PinAoiModalProps) {
  const [name, setName] = useState('');
  const [basin, setBasin] = useState('DJ Basin');
  const [rows, setRows] = useState<TrsRow[]>([blankRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function update(i: number, patch: Partial<TrsRow>) {
    setRows(r => r.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  }
  function addRow() {
    setRows(r => [...r, blankRow()]);
  }
  function removeRow(i: number) {
    setRows(r => r.filter((_, idx) => idx !== i));
  }

  async function submit() {
    setError(null);
    setSubmitting(true);
    try {
      const trs: PinAoiInput['trs'] = rows
        .filter(r => r.township_num && r.range_num && r.section)
        .map(r => ({
          township_num: parseInt(r.township_num, 10),
          township_dir: r.township_dir,
          range_num:    parseInt(r.range_num, 10),
          range_dir:    r.range_dir,
          section:      r.section,
        }));
      if (trs.length === 0) {
        setError('Add at least one TRS entry.');
        setSubmitting(false);
        return;
      }
      const body: PinAoiInput = {
        trs,
        basin: basin.trim() || null,
      };
      if (name.trim()) body.name = name.trim();
      const result = await aoiApi.pin(body);
      onPinned(result.id);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to pin');
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={onClose}
    >
      <div
        className="bg-bg-card border border-border rounded-lg w-[640px] max-w-[92vw] max-h-[88vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-border flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">workspace</div>
            <div className="font-display text-[22px] leading-tight">Pin <em className="text-accent not-italic">AOI</em></div>
          </div>
          <button onClick={onClose} className="text-text-dim hover:text-text text-2xl leading-none">×</button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-mono text-text-faint mb-1.5">
              Name <span className="lowercase text-text-ghost">optional — auto from TRS if empty</span>
            </label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Bashor cluster"
              className="w-full bg-bg-input border border-border px-3 py-2 rounded text-[14px] text-text outline-none focus:border-accent"
            />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider font-mono text-text-faint mb-1.5">Basin</label>
            <select
              value={basin}
              onChange={e => setBasin(e.target.value)}
              className="w-full bg-bg-input border border-border px-3 py-2 rounded text-[14px] text-text outline-none focus:border-accent"
            >
              <option value="">—</option>
              <option value="DJ Basin">DJ Basin</option>
              <option value="Powder River Basin">Powder River Basin</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] uppercase tracking-wider font-mono text-text-faint">TRS entries</label>
              <span className="text-[11px] text-text-ghost">section accepts "23", "23-24", or "23,25,27"</span>
            </div>
            <div className="space-y-2">
              {rows.map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="font-mono text-[11px] text-text-faint w-3">T</span>
                  <input
                    value={r.township_num}
                    onChange={e => update(i, { township_num: e.target.value.replace(/\D/g, '') })}
                    placeholder="4"
                    className="w-14 bg-bg-input border border-border px-2 py-1.5 rounded font-mono text-[13px] text-center outline-none focus:border-accent"
                  />
                  <select
                    value={r.township_dir}
                    onChange={e => update(i, { township_dir: e.target.value as 'N' | 'S' })}
                    className="bg-bg-input border border-border px-1.5 py-1.5 rounded font-mono text-[13px] outline-none focus:border-accent"
                  >
                    <option>N</option>
                    <option>S</option>
                  </select>
                  <span className="font-mono text-[11px] text-text-faint w-3 ml-1">R</span>
                  <input
                    value={r.range_num}
                    onChange={e => update(i, { range_num: e.target.value.replace(/\D/g, '') })}
                    placeholder="62"
                    className="w-14 bg-bg-input border border-border px-2 py-1.5 rounded font-mono text-[13px] text-center outline-none focus:border-accent"
                  />
                  <select
                    value={r.range_dir}
                    onChange={e => update(i, { range_dir: e.target.value as 'E' | 'W' })}
                    className="bg-bg-input border border-border px-1.5 py-1.5 rounded font-mono text-[13px] outline-none focus:border-accent"
                  >
                    <option>W</option>
                    <option>E</option>
                  </select>
                  <span className="font-mono text-[11px] text-text-faint ml-2">Sec</span>
                  <input
                    value={r.section}
                    onChange={e => update(i, { section: e.target.value })}
                    placeholder="23 or 23-24 or 23,25,27"
                    className="flex-1 bg-bg-input border border-border px-2 py-1.5 rounded font-mono text-[13px] outline-none focus:border-accent"
                  />
                  {rows.length > 1 && (
                    <button
                      onClick={() => removeRow(i)}
                      className="text-text-faint hover:text-red text-lg leading-none px-2"
                      title="Remove this entry"
                    >×</button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={addRow}
              className="mt-2 text-[12px] text-accent hover:text-accent-bright font-mono"
            >+ add TRS entry</button>
          </div>

          {error && (
            <div className="text-[12px] text-red bg-red/10 border border-red/40 rounded px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2 bg-bg-card-soft/40">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-[13px] text-text-dim hover:text-text border border-border rounded"
          >Cancel</button>
          <button
            onClick={submit}
            disabled={submitting}
            className="px-4 py-2 text-[13px] bg-accent text-bg font-medium rounded hover:bg-accent-bright disabled:opacity-50"
          >{submitting ? 'Pinning…' : 'Pin AOI'}</button>
        </div>
      </div>
    </div>
  );
}
